import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  AccountBalanceWallet,
  AdminPanelSettings,
  ArrowForward,
  CalendarMonth,
  Campaign,
  Dashboard as DashboardIcon,
  FitnessCenter,
  Groups,
  Notifications,
  People,
  QrCode2,
  Restaurant,
  Search,
  Settings,
  TrendingDown,
} from '@mui/icons-material';

const INK = '#111827';
const MUTED = '#64748B';
const SOFT = '#F6F8FC';
const BORDER = '#E2E8F0';
const BLUE = '#2563EB';
const GREEN = '#059669';
const ORANGE = '#EA580C';
const PURPLE = '#7C3AED';
const ROSE = '#DB2777';

const NAV_ITEMS = [
  { label: 'Dashboard', icon: <DashboardIcon />, active: true },
  { label: 'Users', icon: <People /> },
  { label: 'Batches', icon: <Groups /> },
  { section: 'Content' },
  { label: 'Workout Plans', icon: <CalendarMonth /> },
  { label: 'Meal Plans', icon: <Restaurant /> },
  { label: 'Exercises', icon: <FitnessCenter /> },
  { section: 'Operations' },
  { label: 'Attendance', icon: <QrCode2 /> },
  { label: 'Notifications', icon: <Notifications /> },
  { label: 'Retention', icon: <TrendingDown /> },
  { label: 'Invoices', icon: <AccountBalanceWallet /> },
  { label: 'Gym Settings', icon: <Settings /> },
];

const START_STEPS = [
  {
    title: 'Sign in and confirm your gym',
    text: 'Open the admin panel, check the gym name in your account menu, and review the dashboard counts before making changes.',
    path: 'Admin Login -> Dashboard',
  },
  {
    title: 'Add members and batches',
    text: 'Create members one by one or use batch upload. Assign batch names and preferred times so attendance and follow-ups stay organized.',
    path: 'Users -> Add User / Add Batch',
  },
  {
    title: 'Prepare training content',
    text: 'Create exercises, workout plans, meal plans, and food items that belong to your gym. Publish plans when they are ready for members.',
    path: 'Workout Plans / Meal Plans / Exercises',
  },
  {
    title: 'Run daily operations',
    text: 'Use attendance, invoices, UPI payments, notifications, retention, and gym settings as your everyday operating console.',
    path: 'Operations section',
  },
];

const MODULES = [
  { icon: <People />, title: 'Users', detail: 'Create profiles, update memberships, view analytics, assign plans.', color: BLUE },
  { icon: <QrCode2 />, title: 'Attendance', detail: 'Download the gym QR and review member check-ins by date.', color: GREEN },
  { icon: <CalendarMonth />, title: 'Plans', detail: 'Build workout and meal plans, then assign them to selected members.', color: PURPLE },
  { icon: <TrendingDown />, title: 'Retention', detail: 'Scan for at-risk members and send follow-up nudges.', color: ORANGE },
  { icon: <AccountBalanceWallet />, title: 'Invoices', detail: 'Send fees, verify payments, and manage collection status.', color: ROSE },
  { icon: <Campaign />, title: 'Notifications', detail: 'Send announcements, reminders, offers, and plan updates.', color: '#0F766E' },
];

const FLOW_GROUPS = [
  {
    title: 'Account And Dashboard',
    color: INK,
    flows: [
      { name: 'Login', steps: ['Open Admin Login.', 'Enter email and password.', 'Confirm your name, role, and gym in the top bar.'] },
      { name: 'Read dashboard', steps: ['Open Dashboard.', 'Check members, sessions, attendance, and gym counts.', 'Use the cards to decide what needs attention today.'] },
      { name: 'Find anything quickly', steps: ['Use the top search box.', 'Search by user, gym, or plan name.', 'Open the matching screen from the sidebar if search is not enough.'] },
    ],
  },
  {
    title: 'Gym Setup',
    color: BLUE,
    flows: [
      { name: 'Update gym profile', steps: ['Open My Gym or Gym Settings.', 'Update address, phone, pricing, media, and facilities.', 'Save and review the public/member-facing details.'] },
      { name: 'Set invoice/UPI details', steps: ['Open Invoice Settings.', 'Add UPI ID, QR image, invoice note, and reminder days.', 'Save, then create a test invoice for one member.'] },
      { name: 'Configure notification text', steps: ['Open Gym Settings.', 'Edit templates for invoices, plans, retention, and reminders.', 'Save templates before sending bulk notifications.'] },
    ],
  },
  {
    title: 'Members And Batches',
    color: GREEN,
    flows: [
      { name: 'Add one member', steps: ['Open Users.', 'Click Add User.', 'Enter profile, gym, batch, membership, and contact details.', 'Save and confirm the user appears in the table.'] },
      { name: 'Add many members', steps: ['Open Users.', 'Use batch add/upload.', 'Set batch name and preferred time.', 'Review created members after upload.'] },
      { name: 'Edit or deactivate member', steps: ['Open Users.', 'Search the member.', 'Click edit.', 'Update details or inactive status, then save.'] },
      { name: 'Create a batch', steps: ['Open Batches.', 'Click Add Batch.', 'Set gym, batch name, and preferred time.', 'Assign matching members to that batch.'] },
      { name: 'View member progress', steps: ['Open Users.', 'Open the member analytics action.', 'Review workouts, meals, measurements, streak, and attendance.'] },
    ],
  },
  {
    title: 'Plans And Content',
    color: PURPLE,
    flows: [
      { name: 'Create exercise', steps: ['Open Exercises.', 'Click Add Exercise.', 'Add muscle group, difficulty, instructions, media, and timing.', 'Save to your gym library.'] },
      { name: 'Create workout plan', steps: ['Open Workout Plans.', 'Click Create.', 'Add days, exercises, sets, reps, rest, and tags.', 'Publish when ready.'] },
      { name: 'Create meal plan', steps: ['Open Meal Plans.', 'Click Create or Import.', 'Add meals, foods, calories, macros, and plan goal.', 'Publish when reviewed.'] },
      { name: 'Import strict meal plan', steps: ['Open Meal Plans.', 'Download the template.', 'Fill foods and 30-day plan in Excel.', 'Import and check generated weekly schedules.'] },
      { name: 'Add food item', steps: ['Open Food Items.', 'Click Add Food.', 'Enter category, diet type, macros per 100g, and serving size.', 'Save for meal plan use.'] },
      { name: 'Assign plan to member', steps: ['Open Users or Plans.', 'Select members.', 'Choose workout or meal plan.', 'Assign and confirm notification is sent.'] },
    ],
  },
  {
    title: 'Trainers And Goals',
    color: ORANGE,
    flows: [
      { name: 'Create trainer staff login', steps: ['Open Staff or Trainers.', 'Click Add Trainer.', 'Enter name, email, password, and gym.', 'Share login details with the trainer.'] },
      { name: 'Manage expert trainers', steps: ['Open Trainers.', 'Add trainer profile, specialization, price, and availability.', 'Update or deactivate when needed.'] },
      { name: 'Answer member trainer query', steps: ['Open Trainers.', 'Open Queries.', 'Read the question.', 'Reply and mark it answered.'] },
      { name: 'Create member goal', steps: ['Open Goals.', 'Select member.', 'Add target, date, and notes.', 'Update progress during reviews.'] },
    ],
  },
  {
    title: 'Attendance And Slots',
    color: '#0F766E',
    flows: [
      { name: 'Download attendance QR', steps: ['Open Attendance.', 'Select your gym if needed.', 'Download or display the QR code.', 'Place it at reception for member check-in.'] },
      { name: 'Review attendance', steps: ['Open Attendance.', 'Select date range.', 'Check records and unique member count.', 'Follow up with missing members.'] },
      { name: 'Create slot', steps: ['Open Slot Management.', 'Click Add Slot.', 'Set capacity, time, days, and type.', 'Save and monitor occupancy.'] },
      { name: 'Monitor occupancy', steps: ['Open Slot Management.', 'Review slot occupancy.', 'Adjust capacity or timing if classes are overbooked.'] },
    ],
  },
  {
    title: 'Invoices And Payments',
    color: ROSE,
    flows: [
      { name: 'Create invoice', steps: ['Open Invoices.', 'Click Create Invoice.', 'Select member, title, membership type or amount, and due date.', 'Send invoice.'] },
      { name: 'Send bulk invoices', steps: ['Open Invoices.', 'Select multiple members.', 'Enter fee details.', 'Send and review invoice list.'] },
      { name: 'Verify UPI payment', steps: ['Open UPI Payments.', 'Open pending payment.', 'Check UTR against bank statement.', 'Verify or reject with reason.'] },
      { name: 'Cancel invoice', steps: ['Open Invoices.', 'Find invoice.', 'Open actions.', 'Cancel if it should not be collected.'] },
      { name: 'Review payment stats', steps: ['Open UPI Payments or Payments.', 'Check pending, verified, rejected, and collected totals.', 'Follow up on pending UTRs.'] },
    ],
  },
  {
    title: 'Engagement And Retention',
    color: ORANGE,
    flows: [
      { name: 'Send notification', steps: ['Open Notifications.', 'Choose one member or bulk members.', 'Write title and message.', 'Send or schedule.'] },
      { name: 'Run retention scan', steps: ['Open Retention.', 'Choose gym if needed.', 'Click Scan.', 'Review new high, medium, and low risk alerts.'] },
      { name: 'Nudge at-risk member', steps: ['Open Retention.', 'Open alert.', 'Click Nudge.', 'Use trainer follow-up for high risk members.'] },
      { name: 'Resolve retention alert', steps: ['Open Retention.', 'Confirm member has returned, renewed, or been contacted.', 'Mark alert resolved.'] },
      { name: 'Handle feedback', steps: ['Open Feedback.', 'Filter by status or type.', 'Add admin note.', 'Close or delete when handled.'] },
    ],
  },
  {
    title: 'Admin Controls',
    color: '#475569',
    flows: [
      { name: 'Review audit log', steps: ['Open Audit.', 'Filter by action, user, or date.', 'Export CSV if records are needed.'] },
      { name: 'Superadmin staff management', steps: ['Open Staff.', 'Create admin, gym admin, trainer, or superadmin user.', 'Assign gym where required.'] },
      { name: 'Superadmin gym management', steps: ['Open Gyms.', 'Create gym.', 'Add gym admins.', 'Copy default content if needed.'] },
      { name: 'Database management', steps: ['Open Database.', 'Use only superadmin tools.', 'Preview actions carefully before reset, seed, import, or cleanup.'] },
      { name: 'Gym invoices', steps: ['Open Gym Invoices.', 'Create invoice for a gym.', 'Mark paid or cancel after payment status changes.'] },
    ],
  },
];

export default function AdminGuide() {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.paper', color: INK, width: '100%', overflowX: 'hidden' }}>
      <GuideHeader onLogin={() => navigate('/login')} onHome={() => navigate('/')} />

      <Box sx={{ pt: { xs: 11, md: 13 }, pb: { xs: 6, md: 9 }, bgcolor: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)' }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Grid container rowSpacing={{ xs: 4, md: 6 }} columnSpacing={{ xs: 0, md: 6 }} alignItems="center" sx={{ width: '100%', m: 0 }}>
            <Grid item xs={12} md={5} sx={{ minWidth: 0, pl: { xs: '0 !important', md: undefined }, maxWidth: { xs: '100% !important', md: '41.666667% !important' }, flexBasis: { xs: '100% !important', md: '41.666667% !important' } }}>
              <Chip
                icon={<AdminPanelSettings sx={{ color: `${ORANGE} !important` }} />}
                label="New admin starter guide"
                sx={{ bgcolor: '#FFF7ED', color: ORANGE, border: '1px solid #FED7AA', fontWeight: 900, mb: 2.5 }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 950,
                  fontSize: { xs: '1.55rem', sm: '3rem', md: '4rem' },
                  lineHeight: 1,
                  letterSpacing: 0,
                  overflowWrap: 'break-word',
                  mb: 2,
                }}
              >
                Start running your gym from Fitistic Admin.
              </Typography>
              <Typography sx={{ color: MUTED, fontSize: { xs: 15.5, sm: 17 }, lineHeight: 1.75, mb: 3.5, maxWidth: { xs: 330, sm: 'none' } }}>
                This public guide shows new admins where to begin, how the navigation is organized,
                and which screens to use for daily gym operations.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                  variant="contained"
                  endIcon={<ArrowForward />}
                  onClick={() => navigate('/login')}
                  sx={{ bgcolor: INK, borderRadius: 2, py: 1.35, px: 3, fontWeight: 800, '&:hover': { bgcolor: '#263143' } }}
                >
                  Open Admin Login
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => document.getElementById('guide-steps')?.scrollIntoView({ behavior: 'smooth' })}
                  sx={{ borderColor: BORDER, color: INK, borderRadius: 2, py: 1.35, px: 3, fontWeight: 800, bgcolor: 'background.paper' }}
                >
                  View Steps
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12} md={7} sx={{ minWidth: 0, pl: { xs: '0 !important', md: undefined }, maxWidth: { xs: '100% !important', md: '58.333333% !important' }, flexBasis: { xs: '100% !important', md: '58.333333% !important' } }}>
              <AdminScreenshot />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box id="guide-steps" sx={{ py: { xs: 6, md: 9 }, bgcolor: SOFT, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}` }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <SectionTitle title="First Setup Flow" text="Follow these screens in order when onboarding a new gym admin account." />
          <Grid container spacing={2}>
            {START_STEPS.map((step, index) => (
              <Grid item xs={12} md={3} key={step.title}>
                <Paper elevation={0} sx={{ height: '100%', p: 2.25, border: `1px solid ${BORDER}`, borderRadius: 2, bgcolor: 'background.paper' }}>
                  <Typography sx={{ color: ORANGE, fontWeight: 950, fontSize: 13, mb: 1 }}>0{index + 1}</Typography>
                  <Typography sx={{ fontWeight: 950, fontSize: 17, mb: 1, lineHeight: 1.25 }}>{step.title}</Typography>
                  <Typography sx={{ color: MUTED, fontSize: 13.5, lineHeight: 1.65, mb: 2 }}>{step.text}</Typography>
                  <Chip label={step.path} size="small" sx={{ bgcolor: '#F8FAFC', color: INK, border: `1px solid ${BORDER}`, fontWeight: 800 }} />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6, md: 9 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <SectionTitle title="Navigation Map" text="The left sidebar groups the admin app into setup, content, and daily operations." />
          <Grid container spacing={{ xs: 3, md: 4 }} alignItems="stretch">
            <Grid item xs={12} md={5}>
              <NavigationScreenshot />
            </Grid>
            <Grid item xs={12} md={7}>
              <Grid container spacing={2}>
                {MODULES.map((module) => (
                  <Grid item xs={12} sm={6} key={module.title}>
                    <Paper elevation={0} sx={{ p: 2.25, border: `1px solid ${BORDER}`, borderRadius: 2, height: '100%' }}>
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Box sx={{ width: 42, height: 42, borderRadius: 2, bgcolor: `${module.color}14`, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                          {React.cloneElement(module.icon, { sx: { color: module.color, fontSize: 22 } })}
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 950, fontSize: 16 }}>{module.title}</Typography>
                          <Typography sx={{ color: MUTED, fontSize: 13.5, lineHeight: 1.6, mt: 0.5 }}>{module.detail}</Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6, md: 9 }, bgcolor: SOFT, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}` }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <SectionTitle title="Simple Flow Library" text="Use these short paths whenever a new admin asks, where do I click next?" />
          <Grid container spacing={2.2}>
            {FLOW_GROUPS.map((group) => (
              <Grid item xs={12} md={6} key={group.title}>
                <Paper elevation={0} sx={{ height: '100%', border: `1px solid ${BORDER}`, borderRadius: 2, bgcolor: 'background.paper', overflow: 'hidden' }}>
                  <Box sx={{ px: 2.25, py: 1.8, borderBottom: `1px solid ${BORDER}`, bgcolor: `${group.color}0D` }}>
                    <Typography sx={{ fontWeight: 950, fontSize: 18, color: group.color }}>
                      {group.title}
                    </Typography>
                  </Box>
                  <Stack divider={<Divider />} sx={{ p: 0 }}>
                    {group.flows.map((flow) => (
                      <Box key={flow.name} sx={{ p: 2.25 }}>
                        <Typography sx={{ fontWeight: 950, fontSize: 15.5, mb: 1.25 }}>
                          {flow.name}
                        </Typography>
                        <Stack spacing={0.8}>
                          {flow.steps.map((step, index) => (
                            <Stack key={step} direction="row" spacing={1.1} alignItems="flex-start">
                              <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: `${group.color}14`, color: group.color, display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 950, flexShrink: 0, mt: 0.1 }}>
                                {index + 1}
                              </Box>
                              <Typography sx={{ color: MUTED, fontSize: 13.5, lineHeight: 1.55 }}>
                                {step}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 6, md: 9 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <SectionTitle title="Daily Admin Checklist" text="Use this rhythm after the initial setup is complete." />
          <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Screen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    ['Morning', 'Review dashboard numbers and today\'s attendance.', 'Dashboard, Attendance'],
                    ['Before classes', 'Check batch members and confirm trainer assignments.', 'Batches, Users'],
                    ['After payments', 'Verify UPI claims and send invoices where needed.', 'UPI Payments, Invoices'],
                    ['End of day', 'Scan retention alerts and send nudges to inactive members.', 'Retention, Notifications'],
                  ].map(([time, action, screen]) => (
                    <TableRow key={time}>
                      <TableCell sx={{ fontWeight: 800 }}>{time}</TableCell>
                      <TableCell>{action}</TableCell>
                      <TableCell sx={{ color: BLUE, fontWeight: 800 }}>{screen}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Container>
      </Box>

      <Box sx={{ py: 4.5, bgcolor: '#0B1120' }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box component="img" src="/logo.png" alt="Fitistic" sx={{ width: 32, height: 32, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
              <Typography sx={{ color: '#fff', fontWeight: 900 }}>Fitistic Admin Guide</Typography>
            </Stack>
            <Button variant="contained" onClick={() => navigate('/login')} sx={{ bgcolor: 'background.paper', color: INK, borderRadius: 2, fontWeight: 900, '&:hover': { bgcolor: 'action.hover' } }}>
              Go to Login
            </Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

function GuideHeader({ onLogin, onHome }) {
  return (
    <Box sx={{ position: 'fixed', top: 12, left: { xs: 10, sm: 16 }, right: { xs: 10, sm: 16 }, zIndex: 1000, bgcolor: 'rgba(255,255,255,0.94)', border: `1px solid ${BORDER}`, borderRadius: 3, backdropFilter: 'blur(18px)', boxShadow: '0 12px 34px rgba(15,23,42,0.08)' }}>
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 1.2 }}>
          <Stack direction="row" alignItems="center" spacing={1.25} onClick={onHome} sx={{ cursor: 'pointer' }}>
            <Box component="img" src="/logo.png" alt="Fitistic" sx={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 2 }} />
            <Box>
              <Typography sx={{ fontWeight: 950, color: INK, lineHeight: 1 }}>Fitistic</Typography>
              <Typography sx={{ color: MUTED, fontSize: 12, fontWeight: 700 }}>Admin guide</Typography>
            </Box>
          </Stack>
            <Button variant="contained" onClick={onLogin} sx={{ bgcolor: INK, borderRadius: 2, fontWeight: 850, minWidth: 0, px: { xs: 2, sm: 3 }, whiteSpace: 'nowrap', display: { xs: 'none', sm: 'inline-flex' }, '&:hover': { bgcolor: '#263143' } }}>
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Admin&nbsp;</Box>Login
            </Button>
        </Stack>
      </Container>
    </Box>
  );
}

function SectionTitle({ title, text }) {
  return (
    <Box sx={{ maxWidth: { xs: 330, sm: 680 }, mb: 4 }}>
      <Typography variant="h2" sx={{ fontWeight: 950, fontSize: { xs: '1.85rem', md: '2.55rem' }, lineHeight: 1.12, mb: 1 }}>
        {title}
      </Typography>
      <Typography sx={{ color: MUTED, fontSize: 16, lineHeight: 1.7 }}>{text}</Typography>
    </Box>
  );
}

function AdminScreenshot() {
  return (
    <>
    <Paper elevation={0} sx={{ display: { xs: 'none', sm: 'block' }, border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden', boxShadow: '0 28px 80px rgba(15,23,42,0.14)', bgcolor: '#F5F6FA', width: '100%', maxWidth: '100%' }}>
      <Stack direction="row" sx={{ minHeight: { xs: 390, md: 520 } }}>
        <Box sx={{ width: { xs: 112, sm: 190 }, bgcolor: 'background.paper', borderRight: `1px solid ${BORDER}`, flexShrink: 0, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1.5, borderBottom: `1px solid ${BORDER}` }}>
            <Box component="img" src="/logo.png" alt="" sx={{ width: 26, height: 26 }} />
            <Typography sx={{ fontWeight: 950, fontSize: { xs: 12, sm: 14 } }}>Fitistic</Typography>
          </Stack>
          <List dense sx={{ px: 1, py: 1 }}>
            {NAV_ITEMS.slice(0, 10).map((item, index) => item.section ? (
              <Typography key={item.section} sx={{ color: 'text.secondary', fontSize: 10, fontWeight: 900, textTransform: 'uppercase', px: 1, pt: 1.2, pb: 0.4 }}>
                {item.section}
              </Typography>
            ) : (
              <ListItem key={item.label} disablePadding sx={{ mb: 0.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', px: 1, py: 0.75, borderRadius: 1.5, bgcolor: item.active ? '#EEEDFF' : 'transparent' }}>
                  {React.cloneElement(item.icon, { sx: { fontSize: 15, color: item.active ? '#1a1a2e' : '#9CA3AF' } })}
                  <Typography sx={{ display: { xs: index > 4 ? 'none' : 'block', sm: 'block' }, fontSize: 12, color: item.active ? INK : MUTED, fontWeight: item.active ? 800 : 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item.label}
                  </Typography>
                </Box>
              </ListItem>
            ))}
          </List>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ height: 54, bgcolor: 'background.paper', borderBottom: `1px solid ${BORDER}`, px: { xs: 1.5, sm: 2 } }}>
            <Typography sx={{ fontWeight: 900 }}>Dashboard</Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ display: { xs: 'none', sm: 'flex' } }}>
              <Box sx={{ px: 1.5, py: 0.7, border: `1px solid ${BORDER}`, borderRadius: 2, color: MUTED, fontSize: 12, display: 'flex', gap: 1, alignItems: 'center' }}>
                <Search sx={{ fontSize: 16 }} /> Search users, gyms, plans...
              </Box>
              <Chip label="Gym Admin" size="small" sx={{ bgcolor: '#ECFEFF', color: '#0891B2', fontWeight: 800 }} />
            </Stack>
          </Stack>

          <Box sx={{ p: { xs: 1.25, sm: 2 } }}>
            <Grid container spacing={1.4}>
              {[
                ['Total members', '248', BLUE],
                ['Check-ins', '184', GREEN],
                ['At risk', '12', ORANGE],
              ].map(([label, value, color]) => (
                <Grid item xs={12} sm={4} key={label}>
                  <Paper elevation={0} sx={{ p: 1.6, border: `1px solid ${BORDER}`, borderRadius: 2, bgcolor: 'background.paper', minWidth: 0 }}>
                    <Typography sx={{ color: MUTED, fontSize: 12, fontWeight: 800 }}>{label}</Typography>
                    <Typography sx={{ fontWeight: 950, fontSize: 28, color, lineHeight: 1.1 }}>{value}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Paper elevation={0} sx={{ mt: 1.4, p: 1.75, border: `1px solid ${BORDER}`, borderRadius: 2, bgcolor: 'background.paper' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography sx={{ fontWeight: 950 }}>Recent members</Typography>
                <Button size="small" sx={{ fontWeight: 800, display: { xs: 'none', sm: 'inline-flex' } }}>View users</Button>
              </Stack>
              {['Aarav Sharma', 'Meera Patel', 'Rohan Singh'].map((name, index) => (
                <Stack key={name} direction="row" alignItems="center" spacing={1.4} sx={{ py: 1.1, borderTop: index === 0 ? 0 : '1px solid #F1F5F9' }}>
                  <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: ['#EEEDFF', '#ECFDF5', '#FFF7ED'][index], display: 'grid', placeItems: 'center', fontWeight: 900, fontSize: 12 }}>
                    {name.split(' ').map((part) => part[0]).join('')}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 13 }}>{name}</Typography>
                    <Typography sx={{ color: MUTED, fontSize: 11 }}>{['Active membership', 'Invoice due', 'Plan assigned'][index]}</Typography>
                  </Box>
                  <Chip label={['Active', 'Due', 'New'][index]} size="small" sx={{ fontWeight: 800, display: { xs: 'none', sm: 'inline-flex' } }} />
                </Stack>
              ))}
            </Paper>
          </Box>
        </Box>
      </Stack>
    </Paper>
    <MobileAdminScreenshot />
    </>
  );
}

function MobileAdminScreenshot() {
  return (
    <Paper elevation={0} sx={{ display: { xs: 'block', sm: 'none' }, border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden', boxShadow: '0 18px 45px rgba(15,23,42,0.12)', bgcolor: '#F5F6FA' }}>
      <Box sx={{ bgcolor: 'background.paper', borderBottom: `1px solid ${BORDER}`, p: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <Box component="img" src="/logo.png" alt="" sx={{ width: 24, height: 24 }} />
            <Typography sx={{ fontWeight: 950, fontSize: 14 }}>Dashboard</Typography>
          </Stack>
          <Chip label="Admin" size="small" sx={{ bgcolor: '#ECFEFF', color: '#0891B2', fontWeight: 850 }} />
        </Stack>
      </Box>
      <Box sx={{ p: 1.5 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 1.5, overflowX: 'auto', pb: 0.5 }}>
          {NAV_ITEMS.filter((item) => item.label).slice(0, 5).map((item) => (
            <Chip
              key={item.label}
              icon={React.cloneElement(item.icon, { sx: { fontSize: '16px !important' } })}
              label={item.label}
              sx={{ flexShrink: 0, bgcolor: item.active ? '#EEEDFF' : '#fff', border: `1px solid ${BORDER}`, color: item.active ? INK : MUTED, fontWeight: 800 }}
            />
          ))}
        </Stack>
        <Grid container spacing={1.2}>
          {[
            ['Total members', '248', BLUE],
            ['Check-ins', '184', GREEN],
            ['At risk', '12', ORANGE],
          ].map(([label, value, color]) => (
            <Grid item xs={12} key={label}>
              <Paper elevation={0} sx={{ p: 1.4, border: `1px solid ${BORDER}`, borderRadius: 2, bgcolor: 'background.paper' }}>
                <Typography sx={{ color: MUTED, fontSize: 12, fontWeight: 800 }}>{label}</Typography>
                <Typography sx={{ color, fontWeight: 950, fontSize: 27, lineHeight: 1 }}>{value}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Paper>
  );
}

function NavigationScreenshot() {
  return (
    <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, bgcolor: 'background.paper', overflow: 'hidden', height: '100%' }}>
      <Box sx={{ p: 2, borderBottom: `1px solid ${BORDER}` }}>
        <Typography sx={{ fontWeight: 950 }}>Admin sidebar</Typography>
        <Typography sx={{ color: MUTED, fontSize: 13, mt: 0.4 }}>Actual navigation sections from the admin app.</Typography>
      </Box>
      <List sx={{ p: 1.5 }}>
        {NAV_ITEMS.map((item) => item.section ? (
          <Box key={item.section}>
            <Divider sx={{ my: 1 }} />
            <Typography sx={{ color: 'text.secondary', fontSize: 11, fontWeight: 950, letterSpacing: '0.08em', textTransform: 'uppercase', px: 1, py: 0.5 }}>
              {item.section}
            </Typography>
          </Box>
        ) : (
          <ListItem key={item.label} disablePadding sx={{ mb: 0.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%', px: 1.2, py: 0.85, borderRadius: 2, bgcolor: item.active ? '#EEEDFF' : 'transparent' }}>
              <ListItemIcon sx={{ minWidth: 26, color: item.active ? '#1a1a2e' : MUTED }}>
                {React.cloneElement(item.icon, { sx: { fontSize: 18 } })}
              </ListItemIcon>
              <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14, fontWeight: item.active ? 850 : 650, color: item.active ? INK : MUTED }} />
            </Box>
          </ListItem>
        ))}
      </List>
    </Paper>
  );
}
